import { useForm } from "@conform-to/react";
import { t } from "@lingui/macro";
import { getValibotConstraint, parseWithValibot } from "conform-to-valibot";
import { useActionData } from "react-router";
import * as v from "valibot";
import type { Release } from "~/models/release";
import type { ReleaseCandidateDetails } from "~/models/release-candidate";
import type { action } from "~/routes/_app+/distribution+/new";

const getReReleaseSchema = () =>
  v.variant("showReReleaseFields", [
    v.object({
      showReReleaseFields: v.picklist(["on"]),
      originalReleaseDate: v.pipe(
        v.string(t`A re-release is required.`),
        v.minLength(1, t`A re-release is required.`),
      ),
      ean: v.optional(v.string()),
    }),
    v.object({
      showReReleaseFields: v.picklist(["", "off"]),
    }),
  ]);

const getPreferredReleaseDateSchema = () =>
  v.variant("showCustomReleaseDate", [
    v.object({
      showCustomReleaseDate: v.picklist(["", "off"]),
      preferredReleaseDate: v.pipe(
        v.string(t`A release date is required.`),
        v.minLength(1, t`A release date is required.`),
        v.check((value) => {
          const [year, month, day] = value.split("-").map(Number);
          const date = new Date(year, month - 1, day);
          const minimumDate = new Date();
          minimumDate.setDate(minimumDate.getDate() + 7);
          minimumDate.setHours(0, 0, 0, 0);
          return minimumDate.getTime() <= date.getTime();
        }, t`The release date can't be sooner than in 7 days.`),
      ),
    }),
    v.object({
      showCustomReleaseDate: v.picklist(["on"]),
    }),
  ]);

export const getNewReleaseSchema = () =>
  v.intersect([
    v.object({
      trackPriceCategoryId: v.pipe(
        v.string(t`An option price is required.`),
        v.minLength(1, t`An option price is required.`),
      ),
      albumPriceCategoryId: v.pipe(
        v.string(t`An option price is required.`),
        v.minLength(1, t`An option price is required.`),
      ),
      territories: v.optional(v.picklist(["on", "off", ""])),
      releasePartners: v.optional(v.picklist(["on", "off", ""])),
      dspIds: v.optional(
        v.pipe(
          v.union([v.string(), v.array(v.string())]),
          v.transform((value) => (Array.isArray(value) ? value : [value])),
          v.transform((value) => value.map(Number)),
        ),
      ),
      action: v.optional(
        v.picklist(["save-as-draft", "submit"]),
        "save-as-draft",
      ),
    }),
    getPreferredReleaseDateSchema(),
    getReReleaseSchema(),
  ]);

export type ReleaseInfo = v.InferOutput<ReturnType<typeof getNewReleaseSchema>>;
export type ReleaseInfoFields = keyof ReleaseInfo;

export function useReleaseForm(
  releaseOrReleaseCandidate: Release | ReleaseCandidateDetails,
) {
  const lastResult = useActionData<typeof action>();
  const schema = getNewReleaseSchema();

  return useForm({
    defaultValue: {
      dspIds: releaseOrReleaseCandidate?.dsps.map((dsp) => dsp.id) || [],
      showCustomReleaseDate:
        "preferredReleaseDate" in releaseOrReleaseCandidate &&
        releaseOrReleaseCandidate.preferredReleaseDate
          ? "off"
          : "on",
      preferredReleaseDate:
        "preferredReleaseDate" in releaseOrReleaseCandidate
          ? releaseOrReleaseCandidate.preferredReleaseDate?.substring(0, 10)
          : "",
      showReReleaseFields:
        "originalReleaseDate" in releaseOrReleaseCandidate &&
        releaseOrReleaseCandidate?.originalReleaseDate
          ? "on"
          : "off",
      originalReleaseDate:
        "originalReleaseDate" in releaseOrReleaseCandidate
          ? releaseOrReleaseCandidate?.originalReleaseDate?.substring(0, 10)
          : "",
      ean:
        "ean" in releaseOrReleaseCandidate
          ? releaseOrReleaseCandidate?.ean || ""
          : "",
      trackPriceCategoryId:
        releaseOrReleaseCandidate?.trackPriceCategoryId?.toString() || null,
      albumPriceCategoryId:
        releaseOrReleaseCandidate?.albumPriceCategoryId?.toString() || null,
      territories: "off",
      releasePartners: "on",
    },
    lastResult,
    constraint: getValibotConstraint(schema),
    shouldRevalidate: "onBlur",
    onValidate({ formData }) {
      const validation = parseWithValibot(formData, { schema });

      return validation;
    },
  });
}
